import { prisma } from "@/lib/db";
import {
  BOX_GRADUATED,
  PHASE_2_SESSION_SIZE,
  RETRY_DELAY_PROBLEMS,
} from "./constants";
import { calculatePCorrectFromLogs } from "./predictor";
import type { Phase, SessionProblem, PresentedType } from "./types";

// ============================================================================
// Helpers
// ============================================================================

/**
 * Shuffle array in place (Fisher-Yates)
 */
function shuffle<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// ============================================================================
// Phase 1: Box-Based Session Building
// ============================================================================

interface ProblemWithBox {
  problemId: string;
  problemText: string;
  groupId: string;
  box: number;
  correctCount: number;
  lastResult: string | null;
  pCorrect: number;
}

/**
 * Load problems with their box levels for Phase 1
 */
async function loadProblemsWithBox(userId: string): Promise<ProblemWithBox[]> {
  const now = new Date();

  // Get all problems in focus (boxes 0-3, not graduated)
  const states = await prisma.userProblemState.findMany({
    where: {
      userId,
      box: { not: BOX_GRADUATED },
    },
    include: {
      problem: true,
    },
  });

  // Get recent review logs for p_correct calculation
  const problemIds = states.map(s => s.problemId);
  const logs = await prisma.reviewLog.findMany({
    where: {
      userId,
      problemId: { in: problemIds },
    },
    orderBy: { timestamp: "desc" },
  });

  // Group logs by problemId
  const logsByProblem = new Map<string, Array<{ timestamp: Date; wasCorrect: boolean }>>();
  for (const log of logs) {
    const existing = logsByProblem.get(log.problemId) || [];
    if (existing.length < 10) {
      existing.push({ timestamp: log.timestamp, wasCorrect: log.wasCorrect });
    }
    logsByProblem.set(log.problemId, existing);
  }

  return states.map(s => ({
    problemId: s.problemId,
    problemText: s.problem.problemText,
    groupId: s.problem.groupId,
    box: s.box,
    correctCount: s.correctCount,
    lastResult: s.lastResult,
    pCorrect: calculatePCorrectFromLogs(logsByProblem.get(s.problemId) || [], now),
  }));
}

/**
 * Build session queue for Phase 1 (box-based selection)
 *
 * Selection rules:
 * - Box 0: ALL cards (every round)
 * - Box 1: 50% of cards
 * - Box 2: 1-2 cards
 * - Box 3: 1 card max (quick confirmation)
 */
export async function buildPhase1Session(userId: string): Promise<SessionProblem[]> {
  const problems = await loadProblemsWithBox(userId);

  // Group by box
  const box0 = shuffle(problems.filter(p => p.box === 0));
  const box1 = shuffle(problems.filter(p => p.box === 1));
  const box2 = shuffle(problems.filter(p => p.box === 2));
  const box3 = shuffle(problems.filter(p => p.box === 3));

  const queue: SessionProblem[] = [];
  const usedIds = new Set<string>();

  // Box 0: ALL cards (these need the most work)
  for (const p of box0) {
    queue.push({
      problemId: p.problemId,
      problemText: p.problemText,
      groupId: p.groupId,
      type: "hard",
      pCorrect: p.pCorrect,
      box: p.box,
    });
    usedIds.add(p.problemId);
  }

  // Box 1: 50% of cards
  const box1Count = Math.ceil(box1.length / 2);
  for (let i = 0; i < box1Count && i < box1.length; i++) {
    const p = box1[i];
    queue.push({
      problemId: p.problemId,
      problemText: p.problemText,
      groupId: p.groupId,
      type: "hard",
      pCorrect: p.pCorrect,
      box: p.box,
    });
    usedIds.add(p.problemId);
  }

  // Box 2: 1-2 cards
  const box2Count = Math.min(2, box2.length);
  for (let i = 0; i < box2Count; i++) {
    const p = box2[i];
    queue.push({
      problemId: p.problemId,
      problemText: p.problemText,
      groupId: p.groupId,
      type: "easy",
      pCorrect: p.pCorrect,
      box: p.box,
    });
    usedIds.add(p.problemId);
  }

  // Box 3: 1 card max (quick confirmation before graduation)
  if (box3.length > 0) {
    const p = box3[0];
    queue.push({
      problemId: p.problemId,
      problemText: p.problemText,
      groupId: p.groupId,
      type: "easy",
      pCorrect: p.pCorrect,
      box: p.box,
    });
    usedIds.add(p.problemId);
  }

  // Shuffle the final queue to mix difficulties
  return shuffle(queue);
}

// ============================================================================
// Phase 2: Leitner Interval-Based Session Building
// ============================================================================

/**
 * Build session queue for Phase 2 (due-date based selection)
 *
 * Selection rules:
 * - Query WHERE dueAt <= now
 * - Sort by box ASC (lower = more urgent), then dueAt ASC
 * - Limit to session size (20)
 */
export async function buildPhase2Session(userId: string): Promise<SessionProblem[]> {
  const now = new Date();

  // Get all due problems
  const states = await prisma.userProblemState.findMany({
    where: {
      userId,
      dueAt: { lte: now },
    },
    include: {
      problem: true,
    },
    orderBy: [
      { box: "asc" },
      { dueAt: "asc" },
    ],
    take: PHASE_2_SESSION_SIZE,
  });

  // Get recent review logs for p_correct calculation
  const problemIds = states.map(s => s.problemId);
  const logs = await prisma.reviewLog.findMany({
    where: {
      userId,
      problemId: { in: problemIds },
    },
    orderBy: { timestamp: "desc" },
  });

  // Group logs by problemId
  const logsByProblem = new Map<string, Array<{ timestamp: Date; wasCorrect: boolean }>>();
  for (const log of logs) {
    const existing = logsByProblem.get(log.problemId) || [];
    if (existing.length < 10) {
      existing.push({ timestamp: log.timestamp, wasCorrect: log.wasCorrect });
    }
    logsByProblem.set(log.problemId, existing);
  }

  return states.map(s => ({
    problemId: s.problemId,
    problemText: s.problem.problemText,
    groupId: s.problem.groupId,
    type: (s.box <= 2 ? "hard" : "easy") as PresentedType,
    pCorrect: calculatePCorrectFromLogs(logsByProblem.get(s.problemId) || [], now),
    box: s.box,
  }));
}

// ============================================================================
// Unified Session Builder
// ============================================================================

/**
 * Build a session queue based on current phase
 */
export async function buildSessionQueue(userId: string): Promise<SessionProblem[]> {
  // Get user's current phase
  const userPhase = await prisma.userPhase.findUnique({
    where: { userId },
  });

  const phase: Phase = (userPhase?.currentPhase ?? 1) as Phase;

  if (phase === 1) {
    return buildPhase1Session(userId);
  } else {
    return buildPhase2Session(userId);
  }
}

// ============================================================================
// Session State Management (kept for compatibility)
// ============================================================================

export interface SessionState {
  queue: SessionProblem[];
  position: number;
  retryQueue: string[];
  recentShown: string[];
  consecutiveWrong: number;
}

/**
 * Get next problem from session, handling retries and consecutive wrong
 */
export function getNextFromSession(state: SessionState): {
  problem: SessionProblem | null;
  newState: SessionState;
} {
  // Check for retries first (if enough problems have passed)
  if (state.retryQueue.length > 0) {
    const recentSet = new Set(state.recentShown.slice(-RETRY_DELAY_PROBLEMS));
    const retryIndex = state.retryQueue.findIndex(id => !recentSet.has(id));

    if (retryIndex >= 0) {
      const retryId = state.retryQueue[retryIndex];
      const retryProblem = state.queue.find(p => p.problemId === retryId);

      if (retryProblem) {
        const newRetryQueue = [...state.retryQueue];
        newRetryQueue.splice(retryIndex, 1);

        return {
          problem: { ...retryProblem, type: "retry" },
          newState: {
            ...state,
            retryQueue: newRetryQueue,
            recentShown: [...state.recentShown.slice(-9), retryId],
          },
        };
      }
    }
  }

  // Normal queue progression
  if (state.position >= state.queue.length) {
    return { problem: null, newState: state };
  }

  // Find next problem not recently shown
  for (let i = state.position; i < state.queue.length; i++) {
    const problem = state.queue[i];
    if (!state.recentShown.slice(-3).includes(problem.problemId)) {
      return {
        problem,
        newState: {
          ...state,
          position: i + 1,
          recentShown: [...state.recentShown.slice(-9), problem.problemId],
        },
      };
    }
  }

  // If all remaining were recently shown, just take next
  const problem = state.queue[state.position];
  return {
    problem: problem || null,
    newState: {
      ...state,
      position: state.position + 1,
      recentShown: problem
        ? [...state.recentShown.slice(-9), problem.problemId]
        : state.recentShown,
    },
  };
}

/**
 * Update session state after answer
 */
export function updateSessionAfterAnswer(
  state: SessionState,
  problemId: string,
  wasCorrect: boolean
): SessionState {
  if (wasCorrect) {
    return {
      ...state,
      retryQueue: state.retryQueue.filter(id => id !== problemId),
      consecutiveWrong: 0,
    };
  } else {
    const retryQueue = state.retryQueue.includes(problemId)
      ? state.retryQueue
      : [...state.retryQueue, problemId];

    return {
      ...state,
      retryQueue,
      consecutiveWrong: state.consecutiveWrong + 1,
    };
  }
}
